import React, { useState } from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import { graphql } from 'react-apollo';
import compose from 'lodash.flowright';
import { connect } from 'react-redux';

import { mapProps } from '../../../../../util/hocs';
import Placeholder from '../PhotoSearchUpgradeCheckout/Placeholder';
import { AccountPhotoSearchCheckout } from '../../../../../graphql/queries/account';
import getPhotoSearchCheckoutSearchItems from '../PhotoSearchCheckout/graphql/queries/getPhotoSearchCheckoutSearchItems';
import { PAYMENT_PROCESSOR } from '../PhotoSearchCheckout/helpers';
import usePhotoSearchCheckout from '../PhotoSearchCheckout/hooks/usePhotoSearchCheckout';
import { getErrorString } from '../../../../../util/normalize-errors';
import {
  openAlert as openAlertAction,
  openModal as openModalAction,
  closeModal as closeModalAction,
} from '../../../../../actions/modal-actions';
import PhotoSearchUpgradeIosNotice from '../PhotoSearchUpgradeCheckout/PhotoSearchUpgradeIOSNotice';
import PhotoSearchUpsellSubscriptionSelection from './components/PhotoSearchUpsellSubscriptionSelection';
import usePhotoSearchImmediateCheckout from '../PhotoSearchCheckout/hooks/usePhotoSearchImmediateCheckout';
import CloseIcon from '../../../../../components/icons/CloseIcon';
import Button from '../../../../../components/global/Button';
import PhotoSearchFeatureComparisonModal from './components/PhotoSearchFeatureComparisonModal/PhotoSearchFeatureComparisonModal';
import { ALBUM_SUBTYPE, ALBUM_TYPE } from '../../../../../constants/albums';

const propTypes = {
  className: PropTypes.string,
  account: PropTypes.shape({
    id: PropTypes.string.isRequired,
  }),
  searchSubscriptionItems: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.string.isRequired,
      plan: PropTypes.string.isRequired,
      days: PropTypes.number.isRequired,
      price: PropTypes.shape({
        value: PropTypes.number.isRequired,
      }).isRequired,
    }),
  ),
  openAlert: PropTypes.func.isRequired,
  theme: PropTypes.oneOf(['white']),
  skippable: PropTypes.bool,
  refetchAccountSearchSubscriptions: PropTypes.func.isRequired,
  renderHeader: PropTypes.func,
  onCheckoutSuccess: PropTypes.func,
  dismissible: PropTypes.bool,
  freePlanHidden: PropTypes.bool,
  alertOnSuccess: PropTypes.bool,
  onClose: PropTypes.func,
  onRequestClose: PropTypes.func,
  forSelection: PropTypes.bool,
  onSelect: PropTypes.func,
  albumFolder: PropTypes.shape({
    id: PropTypes.string,
    album_type: PropTypes.oneOf(Object.keys(ALBUM_TYPE)),
    album_subtype: PropTypes.oneOf(Object.keys(ALBUM_SUBTYPE)),
  }),
};

// TODO: We need to make tests for these components. They are key to our business.
// They pretty much contain the same logic as PhotoSearchCheckout, which is tested,
// But needs testing nevertheless

const PhotoSearchUpsell = ({
  account,
  className,
  searchSubscriptionItems,
  openAlert,
  refetchAccountSearchSubscriptions,
  onClose,
  onRequestClose,
  theme,
  skippable,
  renderHeader,
  onCheckoutSuccess,
  dismissible = true,
  freePlanHidden = false,
  alertOnSuccess = true,
  forSelection = false,
  onSelect,
  albumFolder,
}) => {
  const classes = classNames({
    'photo-search-upsell': true,
    'p-0': true,
    [`photo-search-upsell-${theme}`]: theme,
    [className]: !!className,
  });
  const [selectedSearchItem, setSelectedSearchItem] = useState(null);

  const { onCheckout, submitting } = usePhotoSearchCheckout({
    account,
    selectedSearchItem,
    searchSubscriptionItems,
    onCheckoutError: error => {
      openAlert({
        message: getErrorString(error),
        info: error?.request_id || '',
      });
    },
    onCheckoutSuccess: async searchSubscriptionItemPlan => {
      await refetchAccountSearchSubscriptions();

      if (onCheckoutSuccess) {
        onCheckoutSuccess?.({ searchSubscriptionItemPlan, success: true });

        if (alertOnSuccess) {
          openAlert({
            type: 'success',
            title: 'Congratulations!',
            message:
              'Your plan has been upgraded! You can now enjoy newly unlocked features!',
          });
        }

        return;
      }

      onClose?.({ success: true });
    },
  });

  // If the account meets certain criteria (i.e. has enough search credits, or a
  // subscription, etc) we skip the checkout UI and immediately onboard them
  // by calling the `onCheckout` method manually.
  const skipping = usePhotoSearchImmediateCheckout({
    // We want to avoid this from skipping when it doesn't make sense
    // But need to render the hook due to hook rules.
    account: skippable ? account : null,
    onCheckout,
  });

  if (!account || !searchSubscriptionItems || skipping) {
    return <Placeholder />;
  }

  const [activeSearchSubscription] = account.search_subscriptions.records;

  if (
    activeSearchSubscription?.payment_processor === PAYMENT_PROCESSOR.APP_STORE
  ) {
    return (
      <div className={classes}>
        <h2>Looks like your subscription is tied to Apple </h2>
        <PhotoSearchUpgradeIosNotice className="mt-3 ios-notice" />
      </div>
    );
  }

  return (
    <div className={classes}>
      {dismissible && (
        <Button
          btnStyle="link"
          btnSize="lg"
          className="photo-search-upsell-close"
          onClick={() => {
            onClose?.();
            onRequestClose?.();
          }}
        >
          <CloseIcon />
        </Button>
      )}
      {!renderHeader && <h2 className="text-center mb-5">Pick a plan</h2>}

      <PhotoSearchUpsellSubscriptionSelection
        className="mt-3"
        searchSubscriptionItems={searchSubscriptionItems}
        onSelect={({ searchSubscriptionItem }) => {
          setSelectedSearchItem(searchSubscriptionItem);
          onSelect?.({ searchSubscriptionItem });
        }}
        selectedSearchItem={selectedSearchItem}
        onCheckout={onCheckout}
        account={account}
        submitting={submitting}
        activeSearchSubscription={activeSearchSubscription}
        renderHeader={renderHeader}
        freePlanHidden={freePlanHidden}
        forSelection={forSelection}
        albumFolder={albumFolder}
      />
      <PhotoSearchFeatureComparisonModal freePlanHidden={freePlanHidden} />
    </div>
  );
};

PhotoSearchUpsell.propTypes = propTypes;

const withAccount = graphql(AccountPhotoSearchCheckout, {
  props: ({ data: { me, refetch } = {} }) => ({
    account: me,
    refetchAccountSearchSubscriptions: refetch,
  }),
  options: {
    fetchPolicy: 'cache-and-network',
    notifyOnNetworkStatusChange: true,
  },
});

const mapDispatchToProps = {
  openAlert: openAlertAction,
  openModal: openModalAction,
  closeModal: closeModalAction,
};

export { PhotoSearchUpsell as PhotoSearchUpsellComponent };

export default compose(
  connect(null, mapDispatchToProps),
  withAccount,
  getPhotoSearchCheckoutSearchItems,
  mapProps(({ data, ...props }) => ({
    ...props,
    searchSubscriptionItems: data?.searchSubscriptionItems,
  })),
)(PhotoSearchUpsell);
